import {
  CSP_LICENSE_TENANTS_LOADING,
  CSP_LICENSE_TENANTS_LOADED,
  CSP_LICENSE_TENANTS_FAILED,
  CSP_LICENSE_TENANT_DETAIL_LOADING,
  CSP_LICENSE_TENANT_DETAIL_LOADED,
  CSP_LICENSE_TENANT_DETAIL_FAILED,
  CSP_LICENSE_TENANT_DETAIL_NOT_FOUND,
  CSP_LICENSE_TENANT_DETAIL_CLEAR,
  CSP_LICENSE_TENANT_SYNC_TRIGGERING,
  CSP_LICENSE_TENANT_SYNC_TRIGGERED,
  CSP_LICENSE_TENANT_SYNC_FAILED,
  CSP_LICENSE_SETTINGS_LOADING,
  CSP_LICENSE_SETTINGS_LOADED,
  CSP_LICENSE_SETTINGS_SAVING,
  CSP_LICENSE_SETTINGS_SAVED,
  CSP_LICENSE_SETTINGS_FAILED,
  CSP_LICENSE_CART_STAGE,
  CSP_LICENSE_CART_REMOVE,
  CSP_LICENSE_CART_CLEAR,
  CSP_LICENSE_REQUEST_SUBMITTING,
  CSP_LICENSE_REQUEST_SUBMITTED,
  CSP_LICENSE_REQUEST_REJECTED,
  CSP_LICENSE_REQUEST_FAILED,
  CSP_LICENSE_REQUEST_LIFECYCLE_PENDING,
  CSP_LICENSE_REQUEST_LIFECYCLE_DONE,
  CSP_LICENSE_MY_REQUESTS_LOADING,
  CSP_LICENSE_MY_REQUESTS_LOADED,
  CSP_LICENSE_MY_REQUESTS_FAILED,
  CSP_LICENSE_PENDING_APPROVALS_LOADING,
  CSP_LICENSE_PENDING_APPROVALS_LOADED,
  CSP_LICENSE_PENDING_APPROVALS_FAILED,
  CSP_LICENSE_AUDIT_LOG_LOADING,
  CSP_LICENSE_AUDIT_LOG_LOADED,
  CSP_LICENSE_AUDIT_LOG_FAILED,
  CSP_LICENSE_PRODUCTS_LOADING,
  CSP_LICENSE_PRODUCTS_LOADED,
  CSP_LICENSE_PRODUCTS_FAILED,
  CSP_LICENSE_PRODUCTS_MUTATING,
  CSP_LICENSE_PRODUCTS_MUTATED,
} from '../components/actions/cspLicensesTypes'

const initialState = {
  // Tenants list
  tenants: [],
  tenantsLoading: false,
  // Tenant detail
  tenantDetail: null,
  tenantDetailLoading: false,
  tenantDetailNotFound: false,
  // Per-tenant sync trigger state
  syncingTenantId: null,
  // Settings
  settings: null,
  settingsLoading: false,
  settingsSaving: false,
  // Cart - keyed by stockCode so re-staging the same SKU replaces in
  // place rather than duplicating. Entries cleared on tenant-detail
  // navigation away (the page component dispatches clearCart on unmount).
  cart: {
    tenantId: null,
    lines: [],
  },
  // Submit state
  submitting: false,
  lastSubmitResult: null,
  // My Requests
  myRequests: [],
  myRequestsLoading: false,
  // Approvals queue (peer-approval surface for ITManager / SSE / Admin)
  pendingApprovals: [],
  pendingApprovalsLoading: false,
  // Lifecycle action tracking - shows spinner on the active request row
  lifecycleRequestId: null,
  // Activity Log - cross-tenant audit feed
  auditLog: { rows: [], totalCount: 0, page: 1, pageSize: 50 },
  auditLogLoading: false,
  // Product curation
  products: { rows: [], totalCount: 0, page: 1, pageSize: 50 },
  productsLoading: false,
  productsMutating: false,
}

const cspLicensesReducer = (state = initialState, action) => {
  switch (action.type) {
    case CSP_LICENSE_TENANTS_LOADING:
      state.tenantsLoading = true
      return state
    case CSP_LICENSE_TENANTS_LOADED:
      state.tenantsLoading = false
      state.tenants = action.payload
      return state
    case CSP_LICENSE_TENANTS_FAILED:
      state.tenantsLoading = false
      return state

    case CSP_LICENSE_TENANT_DETAIL_LOADING:
      state.tenantDetailLoading = true
      state.tenantDetailNotFound = false
      return state
    case CSP_LICENSE_TENANT_DETAIL_LOADED:
      state.tenantDetailLoading = false
      state.tenantDetail = action.payload
      state.tenantDetailNotFound = false
      return state
    case CSP_LICENSE_TENANT_DETAIL_NOT_FOUND:
      state.tenantDetailLoading = false
      state.tenantDetail = null
      state.tenantDetailNotFound = true
      return state
    case CSP_LICENSE_TENANT_DETAIL_FAILED:
      state.tenantDetailLoading = false
      return state
    case CSP_LICENSE_TENANT_DETAIL_CLEAR:
      state.tenantDetail = null
      state.tenantDetailNotFound = false
      return state

    case CSP_LICENSE_TENANT_SYNC_TRIGGERING:
      state.syncingTenantId = action.payload
      return state
    case CSP_LICENSE_TENANT_SYNC_TRIGGERED:
    case CSP_LICENSE_TENANT_SYNC_FAILED:
      state.syncingTenantId = null
      return state

    case CSP_LICENSE_SETTINGS_LOADING:
      state.settingsLoading = true
      return state
    case CSP_LICENSE_SETTINGS_LOADED:
      state.settingsLoading = false
      state.settings = action.payload
      return state
    case CSP_LICENSE_SETTINGS_SAVING:
      state.settingsSaving = true
      return state
    case CSP_LICENSE_SETTINGS_SAVED:
      // Deliberately does NOT write state.settings. The save action re-fetches,
      // so settings arrive via CSP_LICENSE_SETTINGS_LOADED with the
      // server-computed fields intact. Assigning the request payload here is
      // what made the routing badge read LIVE after every save.
      state.settingsSaving = false
      return state
    case CSP_LICENSE_SETTINGS_FAILED:
      state.settingsLoading = false
      state.settingsSaving = false
      return state

    case CSP_LICENSE_CART_STAGE: {
      const line = action.payload
      // Cart resets when staging on a different tenant - prevents
      // accidentally mixing baskets across tenants if the user navigates
      // sideways without checking out.
      if (state.cart.tenantId && state.cart.tenantId !== line.tenantId) {
        state.cart.lines = []
      }
      state.cart.tenantId = line.tenantId
      const idx = state.cart.lines.findIndex((l) => l.stockCode === line.stockCode)
      if (idx >= 0) {
        state.cart.lines[idx] = line
      } else {
        state.cart.lines.push(line)
      }
      return state
    }
    case CSP_LICENSE_CART_REMOVE: {
      state.cart.lines = state.cart.lines.filter((l) => l.stockCode !== action.payload)
      if (state.cart.lines.length === 0) state.cart.tenantId = null
      return state
    }
    case CSP_LICENSE_CART_CLEAR:
      state.cart.tenantId = null
      state.cart.lines = []
      return state

    case CSP_LICENSE_REQUEST_SUBMITTING:
      state.submitting = true
      state.lastSubmitResult = null
      return state
    case CSP_LICENSE_REQUEST_SUBMITTED:
    case CSP_LICENSE_REQUEST_REJECTED:
      state.submitting = false
      state.lastSubmitResult = action.payload
      return state
    case CSP_LICENSE_REQUEST_FAILED:
      state.submitting = false
      return state

    case CSP_LICENSE_REQUEST_LIFECYCLE_PENDING:
      state.lifecycleRequestId = action.payload
      return state
    case CSP_LICENSE_REQUEST_LIFECYCLE_DONE:
      state.lifecycleRequestId = null
      return state

    case CSP_LICENSE_MY_REQUESTS_LOADING:
      state.myRequestsLoading = true
      return state
    case CSP_LICENSE_MY_REQUESTS_LOADED:
      state.myRequestsLoading = false
      state.myRequests = action.payload
      return state
    case CSP_LICENSE_MY_REQUESTS_FAILED:
      state.myRequestsLoading = false
      return state

    case CSP_LICENSE_PENDING_APPROVALS_LOADING:
      state.pendingApprovalsLoading = true
      return state
    case CSP_LICENSE_PENDING_APPROVALS_LOADED:
      state.pendingApprovalsLoading = false
      state.pendingApprovals = action.payload
      return state
    case CSP_LICENSE_PENDING_APPROVALS_FAILED:
      state.pendingApprovalsLoading = false
      return state

    case CSP_LICENSE_AUDIT_LOG_LOADING:
      state.auditLogLoading = true
      return state
    case CSP_LICENSE_AUDIT_LOG_LOADED:
      state.auditLogLoading = false
      state.auditLog = action.payload
      return state
    case CSP_LICENSE_AUDIT_LOG_FAILED:
      state.auditLogLoading = false
      return state

    case CSP_LICENSE_PRODUCTS_LOADING:
      state.productsLoading = true
      return state
    case CSP_LICENSE_PRODUCTS_LOADED:
      state.productsLoading = false
      state.products = action.payload
      return state
    case CSP_LICENSE_PRODUCTS_FAILED:
      state.productsLoading = false
      state.productsMutating = false
      return state
    case CSP_LICENSE_PRODUCTS_MUTATING:
      state.productsMutating = true
      return state
    case CSP_LICENSE_PRODUCTS_MUTATED:
      state.productsMutating = false
      return state

    default:
      return state
  }
}

export default cspLicensesReducer
